"use client"

import React from "react";
import Image from "next/image";
import Link from "next/link";
import { urlFor } from "@/sanity/lib/image";
import { Button } from "./ui/button";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { PortableText } from "@portabletext/react";

interface CTA {
  text: string;
  actionType: "link" | "cal";
  url?: string;
}

interface FiftyFiftyProps {
  heading?: string;
  subheading?: string;
  body?: any[];
  image?: any;
  imagePosition?: "left" | "right";
  ctaPrimary?: CTA;
  ctaSecondary?: CTA;
  backgroundColor?: string;
  padding?: "sm" | "md" | "lg" | "xl";
  maxWidth?: "sm" | "md" | "lg" | "xl" | "container" | "full";
  styling?: {
    backgroundColor?: string;
    padding?: "sm" | "md" | "lg" | "xl";
    maxWidth?: "sm" | "md" | "lg" | "xl" | "container" | "full";
    imageSizing?: "cover" | "contain" | "natural";
    imageAspectRatio?: "auto" | "square" | "landscape" | "portrait";
  };
}

const FiftyFifty: React.FC<FiftyFiftyProps> = ({
  heading,
  subheading,
  body,
  image,
  imagePosition = "right",
  ctaPrimary,
  ctaSecondary,
  backgroundColor = "",
  padding = "lg",
  maxWidth = "xl",
  styling,
}) => {
  // Check if there's any content to display
  const hasContent = heading || subheading || body || ctaPrimary?.text || ctaSecondary?.text;
  
  if (!hasContent && !image) {
    return null;
  }

  // Dynamic classes
  const bgClass = styling?.backgroundColor || backgroundColor || "";
  const paddingClass = {
    sm: "py-8",
    md: "py-12", 
    lg: "py-16",
    xl: "py-20"
  }[styling?.padding || padding];
  
  const maxWidthClass = {
    container: "",
    sm: "max-w-sm",
    md: "max-w-md",
    lg: "max-w-lg", 
    xl: "max-w-6xl",
    full: "max-w-full"
  }[styling?.maxWidth || maxWidth];

  // Image sizing classes
  const imageSizing = styling?.imageSizing || "cover";
  const imageAspectRatio = styling?.imageAspectRatio || "auto";
  
  const imageObjectFit = {
    cover: "object-cover",
    contain: "object-contain",
    natural: "object-scale-down"
  }[imageSizing];

  const aspectRatioClass = {
    auto: "",
    square: "aspect-square",
    landscape: "aspect-video",
    portrait: "aspect-[3/4]"
  }[imageAspectRatio];

  const contentSection = (
    <div className="flex flex-col justify-center h-full">
      {subheading && (
        <p className="text-sm font-medium text-text-muted mb-2 uppercase tracking-wide">
          {subheading}
        </p>
      )}
      
      {heading && (
        <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-text mb-6 leading-tight">
          {heading}
        </h2>
      )}
      
      {body && body.length > 0 && (
        <div className="text-lg text-text mb-8 leading-relaxed">
          <PortableText value={body} />
        </div>
      )}
      
      {(ctaPrimary?.text || ctaSecondary?.text) && (
        <div className="flex flex-col sm:flex-row gap-4">
          {ctaPrimary?.text && (
            <Button
              variant="default"
              asChild
            >
              {ctaPrimary.actionType === "cal" ? (
                <button
                  type="button"
                  data-cal-link="carlaheywood/15min"
                  data-cal-config='{"layout":"month_view"}'
                  className="inline-flex items-center gap-2"
                  aria-label={ctaPrimary.text || "Book a call"}
                >
                  {ctaPrimary.text}
                </button>
              ) : (
                <Link href={ctaPrimary.url || "#"} className="inline-flex items-center gap-2">
                  {ctaPrimary.text}
                </Link>
              )}
            </Button>
          )}
          
          {ctaSecondary?.text && (
            <Button
              variant="ghost"
              asChild
            >
              {ctaSecondary.actionType === "cal" ? (
                <button
                  type="button"
                  data-cal-link="carlaheywood/15min"
                  data-cal-config='{"layout":"month_view"}'
                  className="inline-flex items-center gap-2"
                  aria-label={ctaSecondary.text || "Book a call"}
                >
                  {ctaSecondary.text}
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" aria-hidden />
                </button>
              ) : (
                <Link href={ctaSecondary.url || "#"} className="inline-flex items-center gap-2">
                  {ctaSecondary.text}
                </Link>
              )}
            </Button>
          )}
        </div>
      )}
    </div>
  );

  const imageSection = image && (
    <div className={`relative h-full min-h-[400px] md:min-h-[500px] ${aspectRatioClass}`}>
      <Image
        src={urlFor(image).width(1200).height(800).url()}
        alt={image.alt || heading || "Section image"}
        fill
        className={`${imageObjectFit} rounded-2xl`}
        sizes="(max-width: 768px) 100vw, 50vw"
      />
    </div>
  );

  return (
    <section className={`${bgClass} ${paddingClass}`}>
      <div className={`container mx-auto px-6 ${maxWidthClass}`}>
        <div className={`grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center ${
          imagePosition === "left" ? "lg:grid-flow-col-dense" : ""
        }`}>
          {imagePosition === "left" ? (
            <>
              {imageSection}
              <div className="lg:col-start-2">
                {contentSection}
              </div>
            </>
          ) : (
            <>
              <div>
                {contentSection}
              </div>
              {imageSection}
            </>
          )}
        </div>
      </div>
    </section>
  );
};

export default FiftyFifty;
